import PropTypes from "prop-types";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// @mui
import { alpha, styled } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
// locales
import { useTranslation } from "react-i18next";
// components
import Iconify from "src/components/iconify";
import { gridColors } from "src/consuman-components/data-grid/styles";
import { TREE_INDENT_WIDTH, TREE_TOGGLE_WIDTH } from "src/consuman-components/data-grid/constants";
import { getFieldValue } from "src/consuman-components/data-grid/core/format";
import {
  buildTree,
  filterTree,
  flattenTree,
  markHasChildren,
  parentKeysOf,
  treeKey,
  treeKeySet,
} from "src/consuman-components/data-grid/core/tree";

const TreeViewRoot = styled("div")(({ theme }) => {
  const colors = gridColors(theme);

  return {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0,
    color: theme.palette.text.primary,
    ...theme.typography.body2,

    "& .ctv-search": {
      height: 40,
      marginBottom: theme.spacing(1),
      padding: theme.spacing(0, 1.5),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.action.hover,
      transition: theme.transitions.create(["background-color", "box-shadow"]),
      "&.Mui-focused": {
        backgroundColor: colors.paper,
        boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
      },
    },

    "& .ctv-list": {
      outline: "none",
    },

    "& .ctv-node": {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
    },

    "& .ctv-toggle": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: `0 0 ${TREE_TOGGLE_WIDTH}px`,
      width: TREE_TOGGLE_WIDTH,
      height: TREE_TOGGLE_WIDTH,
      borderRadius: "50%",
      color: theme.palette.text.secondary,
    },
    "& .ctv-toggle-active": {
      cursor: "pointer",
      transition: theme.transitions.create(["background-color", "color"], {
        duration: theme.transitions.duration.shorter,
      }),
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
    },
    "& .ctv-node-disabled .ctv-toggle": {
      cursor: "default",
      opacity: 0.48,
      "&:hover": { backgroundColor: "transparent", color: theme.palette.text.secondary },
    },

    "& .ctv-checkbox": {
      flexShrink: 0,
    },

    "& .ctv-item": {
      display: "flex",
      alignItems: "center",
      flex: "1 1 auto",
      minWidth: 0,
      minHeight: 48,
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      overflow: "hidden",
      transition: theme.transitions.create("background-color", {
        duration: theme.transitions.duration.shorter,
      }),
      "& > *": { minWidth: 0, maxWidth: "100%" },
    },
    "& .ctv-item-text": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .ctv-clickable .ctv-item": {
      cursor: "pointer",
    },
    "& .ctv-node:not(.ctv-node-disabled) .ctv-item:hover": {
      backgroundColor: colors.hover,
    },
    "& .ctv-node-selected .ctv-item": {
      backgroundColor: colors.selected,
    },
    "& .ctv-node-selected:not(.ctv-node-disabled) .ctv-item:hover": {
      backgroundColor: colors.selectedHover,
    },
    "& .ctv-list:focus-visible .ctv-node-focused .ctv-item": {
      boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.primary.main, 0.48)}`,
    },
    "& .ctv-node-disabled .ctv-item": {
      cursor: "default",
      opacity: 0.48,
    },

    "& .ctv-empty": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1),
      minHeight: 48 * 2,
      color: theme.palette.text.disabled,
    },
  };
});

function addDescendants(node, target) {
  node.children.forEach(child => {
    target.add(treeKey(child.key));
    addDescendants(child, target);
  });
}

const TreeView = forwardRef(function TreeView(props, ref) {
  const {
    dataSource,
    keyExpr = "id",
    parentIdExpr = "parentId",
    displayExpr = "name",
    disabledExpr = "disabled",
    expandedExpr = "expanded",
    searchExpr,
    rootValue = null,
    searchEnabled = false,
    selectionMode = "single",
    showCheckBoxesMode = "none",
    selectByClick = false,
    disableSelectedDescendants = false,
    defaultSelectedKeys,
    itemRender,
    noDataText,
    id,
    className,
    style,
  } = props;

  const { t } = useTranslation();
  const items = useMemo(() => dataSource || [], [dataSource]);
  const multiple = selectionMode === "multiple";
  const showCheckBoxes = multiple && showCheckBoxesMode !== "none";

  const tree = useMemo(
    () => buildTree(items, { keyExpr, parentIdExpr, rootValue }),
    [items, keyExpr, parentIdExpr, rootValue]
  );

  const [search, setSearch] = useState("");
  const [, setRenderVersion] = useState(0);
  const [focusedKey, setFocusedKey] = useState(null);

  const propsRef = useRef(props);
  const treeRef = useRef(tree);
  const instanceRef = useRef(null);
  const listRef = useRef(null);
  const expandedRef = useRef(null);
  const searchExpandedRef = useRef(null);
  const selectedRef = useRef(null);
  const blockedRef = useRef(new Set());
  const knownKeysRef = useRef(new Set());
  const lastTreeRef = useRef(null);
  const lastSearchRef = useRef(null);

  propsRef.current = props;
  treeRef.current = tree;

  if (!instanceRef.current) instanceRef.current = {};

  const computeBlocked = useCallback(
    keys => {
      const blocked = new Set();

      if (!disableSelectedDescendants) return blocked;

      keys.forEach(key => {
        const node = treeRef.current.byKey.get(key);

        if (node) addDescendants(node, blocked);
      });

      return blocked;
    },
    [disableSelectedDescendants]
  );

  if (selectedRef.current === null)
    selectedRef.current = treeKeySet(
      multiple ? defaultSelectedKeys : defaultSelectedKeys?.slice(-1)
    );

  if (expandedRef.current === null) expandedRef.current = new Set();

  if (lastTreeRef.current !== tree) {
    lastTreeRef.current = tree;
    lastSearchRef.current = null;

    tree.nodes.forEach(node => {
      const key = treeKey(node.key);

      if (!knownKeysRef.current.has(key) && getFieldValue(node.data, expandedExpr))
        expandedRef.current.add(key);

      knownKeysRef.current.add(key);
    });

    blockedRef.current = computeBlocked(selectedRef.current);
    blockedRef.current.forEach(key => selectedRef.current.delete(key));
  }

  const term = search.trim().toLowerCase();

  if (lastSearchRef.current !== term) {
    const textExpr = searchExpr || displayExpr;

    lastSearchRef.current = term;
    filterTree(
      tree.nodes,
      term
        ? data => {
            const value = getFieldValue(data, textExpr);

            return value != null && String(value).toLowerCase().includes(term);
          }
        : null
    );
    markHasChildren(tree.root);
    searchExpandedRef.current = term ? new Set(parentKeysOf(tree.nodes)) : null;
  }

  const activeExpansion = () => searchExpandedRef.current || expandedRef.current;

  const isExpanded = key => activeExpansion().has(treeKey(key));
  const isSelected = key => selectedRef.current.has(treeKey(key));
  const isDisabled = node =>
    !!getFieldValue(node.data, disabledExpr) || blockedRef.current.has(treeKey(node.key));

  const rows = flattenTree(tree.root, isExpanded);

  const repaint = useCallback(() => setRenderVersion(version => version + 1), []);

  const apiNode = node => ({
    key: node.key,
    itemData: node.data,
    text: getFieldValue(node.data, displayExpr),
    get selected() {
      return selectedRef.current.has(treeKey(node.key));
    },
    get disabled() {
      return isDisabled(node);
    },
    get expanded() {
      return isExpanded(node.key);
    },
    get parent() {
      return node.parent && !node.parent.isRoot ? apiNode(node.parent) : null;
    },
    get children() {
      return node.children.map(apiNode);
    },
  });

  const commitSelection = (nextKeys, node) => {
    const blocked = computeBlocked(nextKeys);
    const next = new Set(Array.from(nextKeys).filter(key => !blocked.has(key)));
    const previous = selectedRef.current;
    const changed = next.size !== previous.size || Array.from(next).some(key => !previous.has(key));

    blockedRef.current = blocked;

    if (!changed) return;

    selectedRef.current = next;
    repaint();

    propsRef.current.onSelectionChanged?.({
      component: instanceRef.current,
      node: node ? apiNode(node) : null,
      itemData: node?.data,
    });
  };

  const selectNode = (node, selected) => {
    const key = treeKey(node.key);
    let next;

    if (!multiple) next = selected ? new Set([key]) : new Set();
    else {
      next = new Set(selectedRef.current);

      if (selected) next.add(key);
      else next.delete(key);
    }

    commitSelection(next, node);
  };

  const setExpanded = (node, expanded) => {
    if (!node || !node.hasChildren || isDisabled(node)) return;

    const target = activeExpansion();
    const key = treeKey(node.key);

    if (expanded === target.has(key)) return;

    if (expanded) target.add(key);
    else target.delete(key);

    repaint();
  };

  const handleListClick = event => {
    const element = event.target.closest("[data-tree-key]");
    const node = element ? treeRef.current.byKey.get(element.dataset.treeKey) : null;

    if (!node) return;

    setFocusedKey(treeKey(node.key));

    if (event.target.closest(".ctv-toggle")) {
      setExpanded(node, !isExpanded(node.key));
      return;
    }

    if (!selectByClick || isDisabled(node)) return;

    if (multiple) selectNode(node, !isSelected(node.key));
    else if (!isSelected(node.key)) selectNode(node, true);
  };

  const focusedIndex = rows.findIndex(node => treeKey(node.key) === focusedKey);

  const moveFocus = index => {
    const node = rows[Math.max(0, Math.min(index, rows.length - 1))];

    if (node) setFocusedKey(treeKey(node.key));
  };

  const handleKeyDown = event => {
    if (!rows.length) return;

    const current = rows[focusedIndex] || null;

    switch (event.key) {
      case "ArrowDown":
        moveFocus(focusedIndex + 1);
        break;
      case "ArrowUp":
        moveFocus(focusedIndex < 0 ? 0 : focusedIndex - 1);
        break;
      case "Home":
        moveFocus(0);
        break;
      case "End":
        moveFocus(rows.length - 1);
        break;
      case "ArrowRight":
        if (!current) moveFocus(0);
        else if (current.hasChildren && !isExpanded(current.key)) setExpanded(current, true);
        else if (current.hasChildren) moveFocus(focusedIndex + 1);
        break;
      case "ArrowLeft":
        if (!current) moveFocus(0);
        else if (current.hasChildren && isExpanded(current.key)) setExpanded(current, false);
        else if (current.parent && !current.parent.isRoot)
          setFocusedKey(treeKey(current.parent.key));
        break;
      case "Enter":
      case " ":
        if (current && !isDisabled(current)) {
          if (multiple) selectNode(current, !isSelected(current.key));
          else selectNode(current, true);
        }
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  useLayoutEffect(() => {
    if (!focusedKey || !listRef.current) return;

    const element = listRef.current.querySelector(`[data-tree-key="${CSS.escape(focusedKey)}"]`);

    element?.scrollIntoView?.({ block: "nearest" });
  }, [focusedKey]);

  Object.assign(instanceRef.current, {
    getSelectedNodes: () => treeRef.current.nodes.filter(node => isSelected(node.key)).map(apiNode),
    getSelectedNodeKeys: () =>
      treeRef.current.nodes.filter(node => isSelected(node.key)).map(node => node.key),
    getNodes: () => treeRef.current.root.children.map(apiNode),
    selectItem: key => {
      const node = treeRef.current.byKey.get(treeKey(key));

      if (node) selectNode(node, true);

      return !!node;
    },
    unselectItem: key => {
      const node = treeRef.current.byKey.get(treeKey(key));

      if (node) selectNode(node, false);

      return !!node;
    },
    selectAll: () => {
      if (!multiple) return;

      commitSelection(
        new Set(
          treeRef.current.nodes
            .filter(node => !getFieldValue(node.data, disabledExpr))
            .map(node => treeKey(node.key))
        )
      );
    },
    unselectAll: () => commitSelection(new Set()),
    expandItem: key => setExpanded(treeRef.current.byKey.get(treeKey(key)), true),
    collapseItem: key => setExpanded(treeRef.current.byKey.get(treeKey(key)), false),
    expandAll: () => {
      treeRef.current.nodes.forEach(node => {
        if (node.hasChildren) activeExpansion().add(treeKey(node.key));
      });
      repaint();
    },
    collapseAll: () => {
      activeExpansion().clear();
      repaint();
    },
    option: name => propsRef.current[name],
    repaint,
    beginUpdate: () => undefined,
    endUpdate: () => undefined,
    focus: () => listRef.current?.focus(),
    element: () => listRef.current?.parentElement || null,
  });

  useImperativeHandle(ref, () => ({ instance: instanceRef.current }), []);

  const textExpr = displayExpr;

  return (
    <TreeViewRoot
      id={id}
      className={["consuman-tree-view", className].filter(Boolean).join(" ")}
      style={style}
    >
      {searchEnabled ? (
        <InputBase
          className="ctv-search"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={t("gridSearch")}
          inputProps={{ "aria-label": t("gridSearch") }}
          startAdornment={
            <Iconify
              icon="eva:search-fill"
              width={20}
              sx={{ color: "text.disabled", mr: 1, flexShrink: 0 }}
            />
          }
          endAdornment={
            search ? (
              <IconButton
                size="small"
                onClick={() => setSearch("")}
                aria-label={t("clear")}
                sx={{ p: 0.25 }}
              >
                <Iconify
                  icon="mingcute:close-line"
                  width={16}
                />
              </IconButton>
            ) : null
          }
        />
      ) : null}
      <div
        ref={listRef}
        className="ctv-list"
        role="tree"
        aria-multiselectable={multiple || undefined}
        tabIndex={0}
        onClick={handleListClick}
        onKeyDown={handleKeyDown}
        onFocus={event => {
          if (event.target === listRef.current && focusedIndex < 0 && rows.length)
            setFocusedKey(treeKey(rows[0].key));
        }}
      >
        {rows.map(node => {
          const key = treeKey(node.key);
          const expanded = node.hasChildren && isExpanded(node.key);
          const selected = isSelected(node.key);
          const disabled = isDisabled(node);
          const text = getFieldValue(node.data, textExpr);
          const classes = ["ctv-node"];

          if (selected) classes.push("ctv-node-selected");
          if (disabled) classes.push("ctv-node-disabled");
          if (key === focusedKey) classes.push("ctv-node-focused");
          if (selectByClick) classes.push("ctv-clickable");

          return (
            <div
              key={key}
              className={classes.join(" ")}
              role="treeitem"
              aria-level={node.level + 1}
              aria-expanded={node.hasChildren ? expanded : undefined}
              aria-selected={selected}
              aria-disabled={disabled || undefined}
              aria-label={text == null ? undefined : String(text)}
              data-tree-key={key}
              style={node.level ? { paddingLeft: node.level * TREE_INDENT_WIDTH } : undefined}
            >
              <span
                className={`ctv-toggle${node.hasChildren ? " ctv-toggle-active" : ""}`}
                aria-hidden="true"
              >
                {node.hasChildren ? (
                  <Iconify
                    icon={expanded ? "eva:arrow-ios-downward-fill" : "eva:arrow-ios-forward-fill"}
                    width={18}
                  />
                ) : null}
              </span>
              {showCheckBoxes ? (
                <Checkbox
                  size="small"
                  className="ctv-checkbox"
                  checked={selected}
                  disabled={disabled}
                  tabIndex={-1}
                  onClick={event => event.stopPropagation()}
                  onChange={event => {
                    setFocusedKey(key);
                    selectNode(node, event.target.checked);
                  }}
                  inputProps={{ "aria-label": t("selectRow") }}
                  sx={{ p: 0.5, mr: 0.5 }}
                />
              ) : null}
              <div className="ctv-item">
                {itemRender ? itemRender(node.data) : <span className="ctv-item-text">{text}</span>}
              </div>
            </div>
          );
        })}
        {rows.length === 0 ? (
          <div className="ctv-empty">
            <Iconify
              icon="eva:inbox-outline"
              width={22}
            />
            <Typography variant="body2">{noDataText || t("gridNoData")}</Typography>
          </div>
        ) : null}
      </div>
    </TreeViewRoot>
  );
});

TreeView.propTypes = {
  dataSource: PropTypes.array,
  keyExpr: PropTypes.string,
  parentIdExpr: PropTypes.string,
  displayExpr: PropTypes.string,
  disabledExpr: PropTypes.string,
  expandedExpr: PropTypes.string,
  searchExpr: PropTypes.string,
  rootValue: PropTypes.any,
  searchEnabled: PropTypes.bool,
  selectionMode: PropTypes.oneOf(["single", "multiple"]),
  showCheckBoxesMode: PropTypes.oneOf(["none", "normal", "selectAll"]),
  selectByClick: PropTypes.bool,
  disableSelectedDescendants: PropTypes.bool,
  defaultSelectedKeys: PropTypes.array,
  itemRender: PropTypes.func,
  noDataText: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default TreeView;
